import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Heart, EyeOff, ShieldAlert, SearchX, Share2 } from "lucide-react";
import Gallery from "../components/Gallery";
import { ShareSheet } from "../components/ShareSheet";
import { EmptyState, Spinner } from "../components/common";
import { ContactosBody, PropertyBody, GenericBody } from "../components/detail/DetailBodies";
import { ActionBar } from "../components/detail/DetailParts";
import { api } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAppData } from "../context/AppDataContext";
import { useAuth } from "../context/AuthContext";

function RoundBtn({ onClick, children, testid, label }) {
  return (
    <button onClick={onClick} aria-label={label} data-testid={testid}
      className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-navy shadow-md transition-transform duration-200 active:scale-90">
      {children}
    </button>
  );
}

export default function DetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLang();
  const { user } = useAuth();
  const { favorites, toggleFavorite, guardCategory, catKind } = useAppData();
  const [listing, setListing] = useState(null);
  const [status, setStatus] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    if (user === null) return;
    setStatus(null);
    api.get(`/listings/${id}`).then((r) => setListing(r.data)).catch((e) => setStatus(e.response?.status || 500));
  }, [id, user === null, user?.user_id, user?.can_access_adult]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (status === 403) guardCategory("contactos", () => {});
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const back = () => (window.history.length > 1 ? navigate(-1) : navigate("/mercat"));

  if (status) {
    const map = {
      410: [EyeOff, t("detail.unavailable"), t("detail.unavailableSub"), "detail-unavailable"],
      403: [ShieldAlert, t("gate.title"), null, "detail-adult-only"],
    };
    const [icon, title, sub, testid] = map[status] || [SearchX, t("detail.notFound"), null, "detail-not-found"];
    return (
      <div className="flex min-h-[100dvh] flex-col bg-mist pt-safe">
        <div className="p-4"><RoundBtn onClick={back} label="back" testid="detail-back-btn"><ArrowLeft className="h-5 w-5" /></RoundBtn></div>
        <EmptyState icon={icon} title={title} sub={sub} testid={testid}
          action={status === 403
            ? <button className="btn-primary" onClick={() => guardCategory("contactos", () => {})} data-testid="detail-verify-age-btn">{t("gate.verify")}</button>
            : <button className="btn-primary" onClick={() => navigate("/mercat")} data-testid="detail-go-mercat-btn">{t("detail.goMercat")}</button>} />
      </div>
    );
  }
  if (!listing) return <div className="flex min-h-[100dvh] items-center justify-center"><Spinner /></div>;


  const fav = favorites.has(listing.id);
  const kind = catKind(listing.category);
  const Body = { contactos: ContactosBody, property: PropertyBody }[kind] || GenericBody;

  return (
    <div className={`min-h-[100dvh] bg-white md:bg-mist md:pb-10 ${kind === "property" ? "pb-56" : "pb-40"}`} data-testid="detail-page">
      <div className="mx-auto max-w-6xl md:grid md:grid-cols-[1.15fr_1fr] md:gap-8 md:px-6 md:pt-8">
        <div className="relative md:sticky md:top-8 md:self-start">
          <Gallery photos={listing.photos} title={listing.title} tall={kind === "contactos"} counter={kind === "property"} badge={kind === "contactos" ? "+18" : null} />
          <div className="absolute inset-x-0 top-0 flex justify-between p-4 pt-safe-3">
            <RoundBtn onClick={back} label="back" testid="detail-back-btn"><ArrowLeft className="h-5 w-5" /></RoundBtn>
            <div className="flex gap-2.5">
              {kind !== "contactos" && (
                <RoundBtn onClick={() => setShareOpen(true)} label="share" testid="detail-share-btn">
                  <Share2 className="h-5 w-5 text-brand" />
                </RoundBtn>
              )}
              <RoundBtn onClick={() => toggleFavorite(listing.id)} label="favorite" testid="detail-fav-btn">
                <Heart className={`h-5 w-5 text-alert ${fav ? "fill-alert" : ""}`} />
              </RoundBtn>
            </div>
          </div>
          {kind !== "contactos" && <ShareSheet open={shareOpen} onOpenChange={setShareOpen} listing={listing} />}
        </div>

        <div className="px-5 pt-5 md:rounded-3xl md:bg-white md:p-7 md:shadow-[0_6px_24px_rgba(13,42,107,0.08)]">
          {listing.is_owner && listing.status === "paused" && (
            <div className="mb-4 flex items-center gap-2 rounded-2xl bg-alert/10 px-4 py-3 text-sm font-semibold text-alert" data-testid="detail-paused-banner">
              <EyeOff className="h-4 w-4" /> {t("detail.pausedOwner")}
            </div>
          )}
          <Body listing={listing} kind={kind} />
          <ActionBar listing={listing} kind={kind} onEdit={() => navigate(`/editar/${listing.id}`)} />
        </div>
      </div>
    </div>
  );
}
